// pages/UserListWithUpload.jsx
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  Table,
  Tag,
  Button,
  Input,
  Upload,
  Avatar,
  Pagination,
  Card,
  Modal,
  Descriptions,
  message,
} from "antd";
import {
  SearchOutlined,
  FileExcelOutlined,
  UserOutlined,
  EyeOutlined,
} from "@ant-design/icons";

import "./uploadFile.css";
import { routes } from "../../api/routes";
import { callApi } from "../../api/apiCaller";

const UserListWithUpload = () => {
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [previewUser, setPreviewUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const token = useSelector((data) => data.userDataSlice.token);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // --- Logic: Excel Upload ---
  const handleExcelUpload = async (file) => {
    setLoading(true);
    const myHeaders = new Headers();
    myHeaders.append("Authorization", token);

    const formdata = new FormData();
    formdata.append("file", file);

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: formdata,
      redirect: "follow",
    };

    fetch(routes.fileUpload, requestOptions)
      .then((response) => response.json())
      .then((result) => {
        if (result.status === 200) {
          message.success(
            result?.message || "Excel file uploaded successfully!",
          );
          getAllCustomers(); // Refresh list after upload
        } else {
          message.error(result?.message || "Upload failed!");
        }
      })
      .catch((error) => {
        message.error("Network error during upload");
        console.error("error", error);
      })
      .finally(() => setLoading(false));
  };

  const uploadProps = {
    beforeUpload: (file) => {
      handleExcelUpload(file);
      return false;
    },
    accept: ".xlsx,.xls",
    showUploadList: false,
  };

  // --- Logic: Get Customers ---
  const getAllCustomers = () => {
    // Note: Assuming 'callApi' and 'routes' are available globally or imported
    const getRes = (res) => {
      console.log("res of getting data", res);
      setUsers(res?.users || []);
      setPagination((prev) => ({ ...prev, total: res?.totalRecords }));
    };

    callApi(
      "GET",
      `${routes.getAllCustomer}?email=${debouncedSearch}&page=${pagination.current}&limit=${pagination.pageSize}`,
      null,
      setLoading,
      getRes,
      (error) => console.log("error", error),
    );
  };

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchText), 1000);
    return () => clearTimeout(handler);
  }, [searchText]);

  useEffect(() => {
    getAllCustomers();
  }, [pagination.current, pagination.pageSize, debouncedSearch]);

  // --- UI: Table Columns ---
  const columns = [
    {
      title: "User",
      dataIndex: "image",
      key: "image",
      render: (image, record) => (
        <div className="user-cell">
          <Avatar src={image} size={40} icon={<UserOutlined />} />
          <span className="user-name-text">{record.name}</span>
        </div>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag
          color={status === "active" ? "green" : "volcano"}
          className="status-tag"
        >
          {status?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => dayjs(date).format("DD MMM YYYY"),
    },
    {
      title: "Password",
      dataIndex: "password",
      key: "password",
      width: 180,
      render: (password) => (
        <div className="password-copy-wrapper">
          <Input.Password
            value={password}
            readOnly
            bordered={false}
            className="table-password-input"
            onClick={() => {
              navigator.clipboard.writeText(password);
              message.success("Password copied to clipboard!");
            }}
          />
        </div>
      ),
    },
    {
      title: "Preview",
      key: "preview",
      render: (_, record) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          className="preview-btn"
          onClick={() => {
            setPreviewUser(record);
            setIsModalOpen(true);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <div className="user-management-page">
      {/* Topbar: Responsive Search and Upload */}
      <div className="topbar">
        <h2 className="topbar-title">Erchonia User Management</h2>
        <div className="topbar-actions">
          <Input
            placeholder="Search by email..."
            prefix={<SearchOutlined />}
            size="large"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="search-input"
          />
          <Upload {...uploadProps}>
            <Button
              size="large"
              icon={<FileExcelOutlined />}
              className="upload-button"
            >
              Upload Excel
            </Button>
          </Upload>
        </div>
      </div>

      {/* Table Section */}
      <Card className="user-table-card">
        <Table
          columns={columns}
          dataSource={users}
          pagination={false}
          rowKey="_id"
          loading={loading}
          scroll={{ x: 800 }}
          className="custom-user-table"
        />
        <div className="pagination-wrapper">
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            showSizeChanger
            onChange={(page, pageSize) =>
              setPagination({ ...pagination, current: page, pageSize })
            }
          />
        </div>
      </Card>

      {/* Preview Modal for Sample Data Details */}
      <Modal
        title="Full Customer Profile"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={[
          <Button
            key="close"
            type="primary"
            className="modal-close-btn"
            onClick={() => setIsModalOpen(false)}
          >
            Close
          </Button>,
        ]}
        width={700}
      >
        {previewUser && (
          <Descriptions bordered column={{ xs: 1, sm: 2 }}>
            <Descriptions.Item label="Customer ID" span={2}>
              {previewUser.customerId || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Business Name">
              {previewUser.businessName || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Verified">
              <Tag color={previewUser.verified ? "blue" : "default"}>
                {previewUser.verified ? "YES" : "NO"}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="City">
              {previewUser.city}
            </Descriptions.Item>
            <Descriptions.Item label="State">
              {previewUser.state}
            </Descriptions.Item>
            <Descriptions.Item label="Full Address" span={2}>
              {previewUser.address}
            </Descriptions.Item>
            <Descriptions.Item label="Zip Code">
              {previewUser.zipCode}
            </Descriptions.Item>
            <Descriptions.Item label="Devices">
              {previewUser.assignedDevices}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default UserListWithUpload;
